import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Bike, ShoppingBag, Clock, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Home() {
  const navigate = useNavigate();
  const { setMode, setPostcode } = useCart();
  const [tab, setTab] = useState("delivery");
  const [pc, setPc] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null); // null | true | false
  const [settings, setSettings] = useState(null);
  const tapCount = useRef(0);
  const tapTimer = useRef(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const handleLogoTap = () => {
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      navigate("/admin/login");
      return;
    }
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 1500);
  };

  const store = settings?.store_status;
  const ordersOpen = store?.open;
  const closed = !!store && !ordersOpen;
  const deliveryPaused = settings?.delivery_paused;

  useEffect(() => { if (deliveryPaused) setTab("collection"); }, [deliveryPaused]);

  const checkPostcode = async () => {
    if (!pc.trim()) { toast.error("Please enter your postcode"); return; }
    setChecking(true); setResult(null);
    try {
      const { data } = await api.post("/postcode/check", { postcode: pc });
      setResult(data.deliverable);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setChecking(false); }
  };

  const goDelivery = () => { setMode("delivery"); setPostcode(pc.toUpperCase()); navigate("/menu"); };
  const goCollection = () => { setMode("collection"); navigate("/menu"); };

  return (
    <div className="min-h-screen bg-brand-dark grain text-brand-cream">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #3f9c92 0, transparent 45%), radial-gradient(circle at 80% 0, #3f9c92 0, transparent 40%)" }} />
        <div className="relative max-w-lg mx-auto px-6 pt-8 pb-6">
          <div className="flex items-center justify-between">
            <button onClick={handleLogoTap} data-testid="logo-secret-tap" className="cursor-default select-none" aria-label="The Bettle & Chisel">
              <BrandLogo dark size="text-3xl" />
            </button>
            <span className="text-[10px] tracking-[0.2em] uppercase text-brand-teal-light font-semibold">Est. 2026</span>
          </div>

          <div className="mt-10 mb-2">
            <p className="text-brand-teal-light font-semibold text-sm tracking-wide uppercase mb-3">Your local pub at your door</p>
            <h1 className="font-brand text-5xl sm:text-6xl font-bold leading-[0.95] tracking-tight">
              Pub food.<br /><span className="text-brand-teal">Delivered.</span>
            </h1>
            <p className="mt-4 text-brand-cream/70 text-base max-w-sm">
              Order your favourites from The Bettle &amp; Chisel for delivery or collection — {settings?.delivery_area_note || "Delabole & surrounding areas"}.
            </p>
          </div>
        </div>
      </div>

      {/* Order card */}
      <div className="max-w-lg mx-auto px-6 -mt-1 pb-16">
        <div className="bg-brand-cream text-brand-dark rounded-3xl p-5 shadow-2xl">
          {store && !ordersOpen && (
            <div className="mb-4 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3" data-testid="closed-banner">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900 text-sm">{store.message}</p>
                {store.next && <p className="text-amber-700 text-sm mt-0.5">Next available: {store.next}</p>}
                <p className="text-amber-700/80 text-xs mt-2">Online ordering is unavailable right now. Please check back when we reopen.</p>
              </div>
            </div>
          )}

          {!closed && (<>
          {/* Tabs */}
          <div className={`grid ${deliveryPaused ? "grid-cols-1" : "grid-cols-2"} gap-2 p-1 bg-brand-dark/5 rounded-2xl mb-5`}>
            {!deliveryPaused && (
              <button
                data-testid="tab-delivery"
                onClick={() => { setTab("delivery"); setResult(null); }}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${tab === "delivery" ? "bg-brand-teal text-white shadow" : "text-brand-dark/60"}`}>
                <Bike className="w-4 h-4" /> Delivery
              </button>
            )}
            <button
              data-testid="tab-collection"
              onClick={() => { setTab("collection"); setResult(null); }}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${tab === "collection" ? "bg-brand-teal text-white shadow" : "text-brand-dark/60"}`}>
              <ShoppingBag className="w-4 h-4" /> Collection
            </button>
          </div>

          {deliveryPaused && (
            <div className="mb-4 rounded-2xl bg-brand-dark/5 p-3 flex gap-2 items-center justify-center" data-testid="delivery-paused-note">
              <Bike className="w-4 h-4 text-brand-dark/40" />
              <p className="text-sm text-brand-dark/60 font-medium">Delivery is currently unavailable — collection only.</p>
            </div>
          )}

          {tab === "delivery" ? (
            <div>
              <label className="text-sm font-semibold text-brand-dark/70">Enter your postcode</label>
              <div className="flex gap-2 mt-2">
                <div className="relative flex-1">
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-teal" />
                  <Input
                    data-testid="postcode-input"
                    value={pc}
                    onChange={(e) => { setPc(e.target.value); setResult(null); }}
                    onKeyDown={(e) => e.key === "Enter" && checkPostcode()}
                    placeholder="e.g. PL33 9AB"
                    className="pl-9 h-12 rounded-xl uppercase bg-white border-brand-dark/10"
                  />
                </div>
                <Button data-testid="check-postcode-btn" onClick={checkPostcode} disabled={checking}
                  className="h-12 rounded-xl bg-brand-dark hover:bg-brand-dark-2 text-white px-5 font-semibold">
                  {checking ? "…" : "Check"}
                </Button>
              </div>

              {result === true && (
                <div className="mt-4 animate-slide-up" data-testid="delivery-yes">
                  <div className="rounded-2xl bg-brand-teal/10 border border-brand-teal/30 p-4 flex gap-3 items-start">
                    <CheckCircle2 className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" />
                    <p className="font-semibold text-brand-teal-dark">Great news — we deliver to you!</p>
                  </div>
                  <Button data-testid="continue-delivery-btn" onClick={goDelivery} disabled={!ordersOpen}
                    className="w-full mt-3 h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white text-base font-bold active:scale-[0.98] transition-transform">
                    Continue to menu
                  </Button>
                </div>
              )}
              {result === false && (
                <div className="mt-4 animate-slide-up" data-testid="delivery-no">
                  <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex gap-3 items-start">
                    <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="font-medium text-red-800 text-sm">Sorry, we don't currently deliver to this area. You can still order for collection.</p>
                  </div>
                  <Button data-testid="switch-collection-btn" onClick={goCollection} disabled={!ordersOpen}
                    className="w-full mt-3 h-14 rounded-2xl bg-brand-dark hover:bg-brand-dark-2 text-white text-base font-bold">
                    Order for collection instead
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div data-testid="collection-panel">
              <div className="rounded-2xl bg-brand-dark/5 p-4 mb-4">
                <p className="text-sm text-brand-dark/70">Collect fresh from The Bettle &amp; Chisel. Ready in approximately <span className="font-semibold text-brand-dark">{settings?.collection_estimate || "20–30 minutes"}</span>.</p>
              </div>
              <Button data-testid="start-collection-btn" onClick={goCollection} disabled={!ordersOpen}
                className="w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white text-base font-bold active:scale-[0.98] transition-transform">
                Start your order
              </Button>
            </div>
          )}
          </>)}
        </div>

        {/* Feature strip */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          {[["Great pub food", "Made fresh"], ["Delivered fast", "To your door"], ["Local & convenient", settings?.delivery_area_note?.split("&")[0] || "Delabole"]].map(([t, s], i) => (
            <div key={i} className="text-center">
              <p className="text-brand-teal-light font-semibold text-xs">{t}</p>
              <p className="text-brand-cream/50 text-[11px] mt-0.5">{s}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <p className="text-brand-cream/25 text-[11px]">The Bettle &amp; Chisel · {settings?.delivery_area_note || "Delabole & surrounding areas"}</p>
        </div>
      </div>
    </div>
  );
}
